import React, { type ComponentProps, type ReactNode, useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import {
    TeamsAppAnalyticsContext,
    useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import { layers } from '@atlaskit/theme/constants';
import type { ErrorCallback } from '@atlassian/internal-entry-point-container';

import { useAnalyticsEventsWithDuration } from '../hooks/useAnalyticsEventsWithDuration';
import { entryPoint } from '../network/entrypoint';
import { PopupTriggerWithHover } from '../popup-adapter/PopupTriggerWithHover';
import { getPageTime } from '../utils/performance';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import { TeamProfileCardTrigger } from './components/TeamProfileCardTrigger';
import type { TeamProfileCard } from './TeamProfileCard';
import type { TeamProfileCardRelay } from './TeamProfileCardRelay';

type TeamProfileCardWithTriggerProps = Omit<
	ComponentProps<typeof TeamProfileCardRelay>,
	'teamFragment'
> &
	Pick<ComponentProps<typeof PopupTriggerWithHover>, 'placement'> & {
		/**
		 * How the card popup should be triggered.
		 * Options include 'hover', 'click', or both.
		 */
		trigger?: ComponentProps<typeof PopupTriggerWithHover>['triggerMode'];
		/**
		 * Whether to render the popup in the parent DOM node instead of a portal.
		 * Use when the card needs to respect parent overflow or z-index context.
		 */
		shouldRenderToParent?: boolean;
		/**
		 * Trigger element that opens the team profile card on interaction.
		 * Typically a button, link, or team name.
		 */
		children: ReactNode;
	} & Pick<ComponentProps<typeof TeamProfileCard>, 'teamAri'>;

/**
 * A wrapper component that displays a team profile card in a popup when a trigger element
 * is interacted with (hover or click).
 *
 * This component loads the team profile card content lazily using an entry point, displays
 * loading and error states, and tracks analytics events for user interactions. The card data
 * is fetched from GraphQL when the popup is triggered.
 */
function TeamProfileCardWithTrigger({
	teamAri,
	cloudId,
	containerId,
	userId,
	showFlag,
	trigger,
	placement = 'bottom-start',
	shouldRenderToParent = false,
	children,
	additionalActions,
}: TeamProfileCardWithTriggerProps) {
	const fireEvent = useAnalyticsEventsWithDuration();
	const { fireEvent: fireAnalyticsEvent } = useAnalyticsEvents();

	const onErrorBoundary = useCallback(() => {
		fireEvent('ui.teamProfileCard.rendered.errorBoundary', (duration) => ({
			duration,
			firedAt: Math.round(getPageTime()),
		}));
	}, [fireEvent]);

	const onError = useCallback<ErrorCallback>(
		({ error, errorInfo }) => {
			const { name: errorType, message: errorMessage, ...errorRest } = error;
			fireEvent('operational.teamProfileCard.failed.request', (duration) => ({
				duration,
				firedAt: Math.round(getPageTime()),
				gateway: true,
				errorCategory: null,
				errorNumber: null,
				errorPath: null,
				errorStatusCode: null,
				isSLOFailure: true,
				traceId: null,
				errorType,
				errorMessage,
				errorStack: errorInfo.componentStack ?? null,
				...errorRest,
			}));
			fireEvent('ui.teamProfileCard.rendered.error', (duration) => ({
				duration,
				firedAt: Math.round(getPageTime()),
			}));
		},
		[fireEvent],
	);

	const onOpen = useCallback(
		(method: 'hover' | 'click') => {
			fireAnalyticsEvent('ui.teamProfileCard.triggered', {
				method,
				firedAt: Math.round(getPageTime()),
				teamId: teamAri,
			});
		},
		[fireAnalyticsEvent, teamAri],
	);

	if (!teamAri || !cloudId) {
		return <>{children}</>;
	}

	return (
		<ErrorBoundary onError={onErrorBoundary} fallback={<>{children}</>}>
			<PopupTriggerWithHover
				entryPoint={entryPoint}
				placement={placement}
				fallback={<CardLoading />}
				onError={onError}
				onOpen={onOpen}
				errorFallback={CardError}
				entryPointParams={{ cloudId, teamId: teamAri }}
				entryPointProps={{ cloudId, containerId, userId, showFlag, additionalActions }}
				shouldRenderToParent={shouldRenderToParent}
				shouldUseCaptureOnOutsideClick={true}
				zIndex={layers.modal()}
				triggerMode={trigger}
				trigger={(triggerProps) =>
					<TeamProfileCardTrigger trigger={trigger} triggerProps={triggerProps}>
						{children}
					</TeamProfileCardTrigger>
				}
			/>
		</ErrorBoundary>
	);
}

const AnalyticsWrapper = (props: TeamProfileCardWithTriggerProps): React.JSX.Element => {
	return (
		<TeamsAppAnalyticsContext
			data={{ source: 'teamProfileCardTrigger', attributes: { isRenderedByTrigger: true } }}
		>
			<TeamProfileCardWithTrigger {...props} />
		</TeamsAppAnalyticsContext>
	);
};

export { AnalyticsWrapper as TeamProfileCardWithTrigger };
